import * as React from 'react';
import { useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Box from '@mui/joy/Box';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import BookingStatusChip from './BookingStatusChip';
import GeneralApiService from "../../../axios/Axios";
import { toast } from 'react-toastify';

type MessagesPaneHeaderProps = {
    sender: UserProps;
    category?: string;
    onOptOut?: () => void;
    contentPaddingX?: number;
    onBack?: () => void;
    ronaldScope?: 'lead' | 'booking' | 'none';
    ronaldAiEnabled?: boolean;
    onToggleRonaldAi?: (nextEnabled: boolean) => void;
    togglingRonaldAi?: boolean;
    latestBookingId?: number | null;
    latestBookingStatus?: string | null;
    latestBookingBookedIn?: string | null;
};

export default function MessagesPaneHeader(props: MessagesPaneHeaderProps) {
    const {
        sender,
        category,
        onOptOut,
        contentPaddingX,
        onBack,
        ronaldScope,
        ronaldAiEnabled,
        onToggleRonaldAi,
        togglingRonaldAi,
        latestBookingId,
        latestBookingStatus,
        latestBookingBookedIn,
    } = props;
    const [optOutModalOpen, setOptOutModalOpen] = useState(false);
    const [optingOut, setOptingOut] = useState(false);
    const hasBooking = !!latestBookingId;

    const handleOptOut = async () => {
        setOptingOut(true);
        try {
            const response = await GeneralApiService.optOutCustomer(sender.id);
            toast.success(`${response.customer_name} has been opted out`);
            setOptOutModalOpen(false);
            if (onOptOut) onOptOut();
        } catch (error: any) {
            console.error('Failed to opt out customer:', error);
            toast.error(error.response?.data?.error || 'Failed to opt out customer');
        } finally {
            setOptingOut(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2,
                    px: contentPaddingX != null ? contentPaddingX : 2,
                    borderBottom: '1px solid',
                    borderColor: '#e5e7eb',
                    bgcolor: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    flexShrink: 0,
                    minWidth: 0,
                }}
            >
                <Box sx={{minWidth: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 0.5}}>
                        {onBack && (
                            <IconButton
                                variant="plain"
                                color="neutral"
                                size="sm"
                                sx={{display: {xs: 'inline-flex', sm: 'none'}}}
                                onClick={onBack}
                            >
                                <ArrowBackIosNewRoundedIcon/>
                            </IconButton>
                        )}
                        <Typography
                            component="h2"
                            noWrap
                            sx={{fontWeight: 700, fontSize: '18px'}}
                        >
                            {sender.name}
                        </Typography>
                        {category && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: 1,
                                    py: '2px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    lineHeight: 1.4,
                                    borderRadius: '9999px',
                                    ...(category === 'Existing Booking'
                                        ? {bgcolor: '#dcfce7', color: '#15803d'}
                                        : {bgcolor: '#fef3c7', color: '#b45309'}),
                                }}
                            >
                                {category === 'Existing Booking' ? 'Booking' : 'Lead'}
                            </Box>
                        )}
                        {hasBooking && (
                            <BookingStatusChip
                                status={latestBookingStatus}
                                bookedIn={latestBookingBookedIn}
                                size="md"
                            />
                        )}
                    </Box>
                    <Typography level="body-sm" sx={{color: '#6b7280', fontSize: '14px'}}>
                        {sender.phone}{sender.email ? ` • ${sender.email}` : ''}
                    </Typography>
                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0}}>
                    {ronaldScope && ronaldScope !== 'none' && onToggleRonaldAi && (
                        <Box
                            component="label"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                cursor: 'pointer',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                px: 1.25,
                                py: 0.75,
                                transition: 'all 0.15s',
                                '&:hover': {bgcolor: '#f8fafc'},
                            }}
                        >
                            <SmartToyRoundedIcon sx={{fontSize: 14, color: '#a855f7'}}/>
                            <Box
                                component="input"
                                type="checkbox"
                                checked={!!ronaldAiEnabled}
                                disabled={!!togglingRonaldAi}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleRonaldAi(e.target.checked)}
                                sx={{
                                    accentColor: '#7c3aed',
                                    width: 14,
                                    height: 14,
                                    cursor: 'pointer',
                                }}
                            />
                            <Typography level="body-xs" sx={{fontWeight: 500, color: '#475569', fontSize: '12px', whiteSpace: 'nowrap'}}>
                                Ronald - {ronaldScope === 'booking' ? 'Booking' : 'Lead'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Modal open={optOutModalOpen} onClose={() => setOptOutModalOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 1 }}>
                        Opt Out Customer
                    </Typography>
                    <Typography level="body-md" sx={{ mb: 2 }}>
                        Are you sure you want to opt out <strong>{sender.name}</strong>?
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                        This will:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2, fontSize: 'sm' }}>
                        <li>Add "Opt Out" status to all their leads</li>
                        <li>Mark them as unsubscribed</li>
                        <li>Send them an opt-out confirmation SMS</li>
                        <li>Remove them from bulk SMS campaigns</li>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button variant="plain" color="neutral" onClick={() => setOptOutModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleOptOut}
                            loading={optingOut}
                        >
                            Opt Out
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </>
    );
}
